import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useLogin } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import type { MfaChallenge } from '@/types';

/**
 * The sign-in form.
 *
 * Worth reading as the pattern for every form in this codebase: Formik owns the
 * field state and submission, Yup states the rules, and a 422 from the server is
 * mapped back onto the fields that caused it.
 *
 * The server validates every request with Zod regardless. Yup here is for
 * immediate feedback, never a decision.
 */
const schema = Yup.object({
  email: Yup.string()
    .trim()
    .required('Enter your email address.')
    // Deliberately not a stricter pattern. Over-strict email rules reject valid
    // addresses - plus-addressing, new TLDs - and the server has the last word.
    .email('Enter a valid email address.')
    .max(320, 'That address is too long.'),
  password: Yup.string().required('Enter your password.'),
});

export function LoginForm({ onChallenge }: { onChallenge: (challenge: MfaChallenge) => void }) {
  const login = useLogin();

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      login.mutate(
        { email: values.email.trim(), password: values.password },
        {
          onSuccess: (result) => {
            // Password accepted, second factor outstanding. Nothing is stored;
            // the page swaps to the code step.
            if (!('accessToken' in result)) onChallenge(result);
          },
          onError: (error) => {
            /*
             * A 422 arrives keyed by request path - `body.email` - so it can be
             * shown against the input that caused it rather than as a banner.
             */
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
          },
        },
      );
    },
  });

  const apiError = login.error instanceof ApiError ? login.error : null;

  /**
   * A banner for anything that is not about one field.
   *
   * Suppressed when field errors exist, because those are shown in place - but
   * only when at least one of them could be placed. An error with no input to
   * attach it to would otherwise fail completely silently.
   */
  const banner = (): string | null => {
    if (!apiError) return null;

    if (apiError.fieldErrors) {
      const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
      return unplaced.length > 0 ? apiError.message : null;
    }

    if (apiError.code === 'invalid_credentials') return ERRORS.invalidCredentials;
    if (apiError.code === 'account_locked') return ERRORS.accountLocked;
    if (apiError.code === 'rate_limited') return ERRORS.rateLimited;
    return apiError.message;
  };

  /** Shown once touched, so the form does not scold somebody mid-typing. */
  const error = (field: 'email' | 'password'): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  const message = banner();

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      {message && (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {message}
        </p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          {...formik.getFieldProps('email')}
          aria-invalid={Boolean(error('email'))}
          aria-describedby={error('email') ? 'email-error' : undefined}
        />
        {error('email') && (
          <p id="email-error" className="text-sm text-destructive">
            {error('email')}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          {...formik.getFieldProps('password')}
          aria-invalid={Boolean(error('password'))}
          aria-describedby={error('password') ? 'password-error' : undefined}
        />
        {error('password') && (
          <p id="password-error" className="text-sm text-destructive">
            {error('password')}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </Button>

      {/*
        The request id is what support uses to find this exact request in the
        server logs, so it is worth showing rather than hiding.
      */}
      {apiError?.requestId && (
        <p className="text-center font-mono text-xs text-muted-foreground">
          Reference: {apiError.requestId}
        </p>
      )}
    </form>
  );
}
